import type React from 'react';
import type { SnapCarouselResult } from 'react-snap-carousel';
import { mergeProps } from '@base-ui/react/merge-props';
import { useRender } from '@base-ui/react/use-render';
import clsx from 'clsx';
import { times } from 'es-toolkit/compat';
import { useReducedMotion } from 'motion/react';
import { createContext, use, useEffect, useMemo, useRef } from 'react';
import { useSnapCarousel } from 'react-snap-carousel';
import { useIsomorphicLayoutEffect } from '@_/hooks';
import { Bleed } from '../Bleed/index.ts';

type CarouselContextType = Pick<
  SnapCarouselResult,
  'activePageIndex' | 'hasNextPage' | 'hasPrevPage' | 'next' | 'prev' | 'pages' | 'scrollRef'
>;

const CarouselContext = createContext<CarouselContextType>({} as CarouselContextType);

export type CarouselProps = React.PropsWithChildren<{
  initialIndex?: number;
  onChange?: (index: number) => void;
}>;

export function Carousel({ children, initialIndex, onChange }: CarouselProps) {
  const { activePageIndex, goTo, hasNextPage, hasPrevPage, next, pages, prev, scrollRef } =
    useSnapCarousel();

  const goToRef = useRef(goTo);
  goToRef.current = goTo;

  useIsomorphicLayoutEffect(() => {
    if (typeof initialIndex === 'number') {
      const timeout = setTimeout(() => goToRef.current(initialIndex, { behavior: 'instant' }), 0);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [initialIndex]);

  useEffect(() => {
    onChange?.(activePageIndex);
  }, [activePageIndex, onChange]);

  return (
    <CarouselContext
      value={useMemo(
        () => ({
          activePageIndex,
          hasNextPage,
          hasPrevPage,
          next,
          prev,
          pages,
          scrollRef
        }),
        [activePageIndex, hasNextPage, hasPrevPage, next, prev, pages, scrollRef]
      )}
    >
      {children}
    </CarouselContext>
  );
}

export interface CarouselSlidesProps extends React.ComponentPropsWithoutRef<'div'> {
  bleed?: boolean | 'mobile';
}

export function CarouselSlides({ bleed, children, ...rest }: CarouselSlidesProps) {
  const { scrollRef } = use(CarouselContext);
  const baseProps: React.ComponentPropsWithRef<'div'> = {
    ...rest,
    ref: scrollRef,
    className: clsx(
      'relative flex h-full snap-x snap-mandatory scrollbar-none items-stretch overflow-auto',
      bleed && 'scroll-px-(--gutter-x)',
      bleed === 'mobile' && 'sm:scroll-px-0',
      rest.className
    )
  };
  return bleed ? (
    <Bleed {...baseProps} horizontal mobileOnly={bleed === 'mobile'}>
      {children}
    </Bleed>
  ) : (
    <div {...baseProps}>{children}</div>
  );
}

export interface CarouselItemProps {
  snap?: 'start' | 'center' | 'end';
}

export function CarouselSlide({
  render,
  snap = 'center',
  ...rest
}: useRender.ComponentProps<'div'> & CarouselItemProps) {
  return useRender({
    defaultTagName: 'div',
    render,
    props: {
      ...rest,
      className: clsx(
        'shrink-0',
        snap === 'start' && 'snap-start',
        snap === 'center' && 'snap-center',
        snap === 'end' && 'snap-end',

        // Hack to add padding to the right of the last card
        // Credit: https://alexandergottlieb.com/2018/02/22/overflow-scroll-and-the-right-padding-problem-a-css-only-solution/
        'after:absolute after:-right-(--gutter-x) after:block after:h-px after:w-(--gutter-x) sm:after:hidden',

        rest.className
      )
    }
  });
}

export function CarouselDots() {
  const { activePageIndex, pages } = use(CarouselContext);

  return (
    <div className="flex justify-center gap-x-1">
      {times(pages.length).map(index => (
        <div
          key={index}
          className={clsx(
            'h-1.5 w-1.5 rounded-full bg-white',
            index !== activePageIndex && 'bg-white/60'
          )}
        />
      ))}
    </div>
  );
}

export function CarouselSlideCounter() {
  const { activePageIndex, pages } = use(CarouselContext);
  return (
    <>
      {activePageIndex + 1}/{pages.length}
    </>
  );
}

export function CarouselNextButton({
  alwaysVisible,
  behavior,
  render,
  ...rest
}: useRender.ComponentProps<'button'> & Omit<UseControlButtonOptions, 'direction'>) {
  const { buttonProps, isVisible } = useControlButton({
    alwaysVisible,
    behavior,
    direction: 'next'
  });
  const element = useRender({
    defaultTagName: 'button',
    render,
    enabled: isVisible,
    props: mergeProps<'button'>(buttonProps, rest)
  });
  return element ?? <div />;
}

export function CarouselPrevButton({
  alwaysVisible,
  behavior,
  render,
  ...rest
}: useRender.ComponentProps<'button'> & Omit<UseControlButtonOptions, 'direction'>) {
  const { buttonProps, isVisible } = useControlButton({
    alwaysVisible,
    behavior,
    direction: 'prev'
  });
  const element = useRender({
    defaultTagName: 'button',
    render,
    enabled: isVisible,
    props: mergeProps<'button'>(buttonProps, rest)
  });
  return element ?? <div />;
}

interface UseControlButtonOptions {
  alwaysVisible?: boolean;
  behavior?: ScrollBehavior;
  direction: 'next' | 'prev';
}

function useControlButton({
  alwaysVisible = false,
  behavior = 'smooth',
  direction
}: UseControlButtonOptions) {
  const { hasNextPage, hasPrevPage, next, prev } = use(CarouselContext);
  const shouldReduceMotion = useReducedMotion();

  const hasMore = (direction === 'prev' && hasPrevPage) || (direction === 'next' && hasNextPage);
  const resolvedBehavior = shouldReduceMotion ? 'instant' : behavior;

  const buttonProps = {
    disabled: !hasMore,
    onClick: () =>
      direction === 'next'
        ? next({ behavior: resolvedBehavior })
        : prev({ behavior: resolvedBehavior }),
    type: 'button'
  } satisfies React.ComponentPropsWithoutRef<'button'>;

  return {
    buttonProps,
    isVisible: alwaysVisible || hasMore
  };
}
